import { useTranslation } from 'react-i18next';
import styles from './NarrativePage.module.scss';

const formatSecondsToMinutes = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Two kinds of "no ETA":
//  - import process, eta === null  -> backend is still estimating, show "Estimating…"
//  - indeterminate (manual update) -> ETA is unknowable, show label + animated bar
export const PopulationProgressBar = ({
  value,
  max = 100,
  label,
  status,
  eta = null,
  indeterminate = false,
  hint = 'Reload page to see imported content',
}) => {
  const { t } = useTranslation();

  const isFailed = status === 'FAILED';
  const isFinished = status === 'DONE' || status === 'FAILED';
  const isEstimating = !indeterminate && eta === null && !isFinished;
  const showLoadingStripe = !isFailed && (indeterminate || isEstimating);
  const percent = Math.min(100, Math.max(0, ((value || 0) / max) * 100));

  const renderHeader = () => {
    if (isFailed) {
      return (
        <>
          <div className={styles.populationProgressBarHeaderLeft}>
            <div>{t('Data import has failed')}</div>
          </div>
          <div className={styles.populationProgressBarHeaderRight}>
            <span>{t('Try to restart the case import (Edit -> Refresh whole case history)')}</span>
          </div>
        </>
      );
    }

    if (isEstimating) {
      return (
        <div className={styles.populationProgressBarHeaderLeft}>
          <div>{t('Estimating publication import time')}</div>
        </div>
      );
    }

    return (
      <>
        <div className={styles.populationProgressBarHeaderLeft}>
          <div>{t(label)}</div>
          {!indeterminate && (
            <span>
              {value || 0}% {t('complete')}
            </span>
          )}
          {!indeterminate && eta ? (
            <span>({t('ETA', { count: formatSecondsToMinutes(eta) })})</span>
          ) : null}
        </div>
        <div className={styles.populationProgressBarHeaderRight}>
          <span>{t(hint)}</span>
        </div>
      </>
    );
  };

  return (
    <div
      className={`${styles.populationProgressBarContainer} ${isFailed ? styles.populationProgressFailed : ''}`}
      role="status"
      aria-live="polite"
    >
      <div className={styles.populationProgressBarHeader}>{renderHeader()}</div>

      <div className={styles.populationProgressBar}>
        {showLoadingStripe ? (
          <div
            className={styles.populationProgressBarLoading}
            role="progressbar"
            aria-label={t(isEstimating ? 'Estimating publication import time' : label)}
          ></div>
        ) : (
          <div
            className={styles.customProgressBar}
            role="progressbar"
            aria-label={t(isFailed ? 'Data import has failed' : label)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(percent)}
          >
            <div
              className={styles.customProgressBarFill}
              style={{ width: `${percent}%` }}
            ></div>
          </div>
        )}
      </div>
    </div>
  );
};
